import './HvacPage.scss';
import { HvacArea } from '@belimo-retrofit-portal/logic';
import { useForm, useFormWatch } from '@form-ts/react';
import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';
import { useAction } from 'src/hooks/useAction';
import { useBoundAction } from 'src/hooks/useBoundAction';
import { useParametricSelector } from 'src/hooks/useParametricSelector';
import { useIntl } from 'src/migration/react-intl';
import { BottomNavigationHelper } from 'src/modules/common/components/BottomNavigationHelper';
import { ProjectToolbar } from 'src/modules/common/components/ProjectToolbar';
import { AppLayout } from 'src/modules/common/layouts/AppLayout';
import { useFormResolver } from 'src/modules/form/hooks/useFormResolver';
import { FormError } from 'src/modules/form/types/FormError';
import { HVAC_CHANGE } from 'src/modules/hvac/actions/HvacActions';
import { getHvacFormData } from 'src/modules/hvac/selectors/getHvacFormData';
import { getHvacFormResolver } from 'src/modules/hvac/selectors/getHvacFormResolver';
import { HvacFormData } from 'src/modules/hvac/types/HvacFormData';
import { HvacSettingsView } from 'src/modules/hvac/views/HvacSettingsView';
import { PROJECT_VIEW_SHARE } from 'src/modules/project-view/actions/ProjectViewActions';
import { getProjectDetails } from 'src/modules/project-view/selectors/getProjectDetails';
import { ShareProject } from 'src/modules/sharing-projects/containers/ShareProject';

export const HvacSettingsPage = (): React.ReactElement => {
  const intl = useIntl();
  const project = useSelector(getProjectDetails);

  const formData = useParametricSelector(getHvacFormData, project);
  const formResolver = useParametricSelector(getHvacFormResolver, project);
  const saveFormData = useAction(HVAC_CHANGE.trigger);
  const shareProject = useBoundAction(PROJECT_VIEW_SHARE.trigger, project);

  const hvacForm = useForm<HvacFormData, FormError>('hvac', {
    reinitialize: false,
    initialValues: formData,
  });
  useFormResolver(hvacForm, formResolver);

  const areasField = hvacForm.field.at('assessment').at('areas');
  const heatingEnabled = useFormWatch(hvacForm, areasField.at(HvacArea.HEATING).at('enabled').value.get);
  const coolingEnabled = useFormWatch(hvacForm, areasField.at(HvacArea.COOLING).at('enabled').value.get);
  const ventilationEnabled = useFormWatch(hvacForm, areasField.at(HvacArea.VENTILATION).at('enabled').value.get);

  const tabsDisabled = !heatingEnabled && !coolingEnabled && !ventilationEnabled;

  useEffect(() => hvacForm.subscribe((prevState) => {
    const nextState = hvacForm.currentState;
    if (prevState.values !== nextState.values) {
      saveFormData(nextState.values);
    }
  }), [hvacForm, saveFormData]);

  return (
    <>
      <Helmet>
        <title>
          {intl.formatMessage({ id: 'hvac/pageTitle' })}
        </title>
      </Helmet>

      <AppLayout>
        <ProjectToolbar
          project={project}
          onShare={shareProject}
        />

        <div className="bp-hvac-page">
          <div className="bp-hvac-page__content">
            <HvacSettingsView form={hvacForm} readOnly={project.readOnly}/>

            <BottomNavigationHelper
              routeBackText={intl.formatMessage({ id: 'common/bottomNavigation/hvac/settings/back' })}
              routeBackPath={`/project/${encodeURIComponent(project.id)}`}
              routeForthText={intl.formatMessage({ id: 'common/bottomNavigation/hvac/settings/next' })}
              routeForthPath={`/project/${encodeURIComponent(project.id)}/hvac/emission`}
              nextRouteDisabled={tabsDisabled}
            />
          </div>
        </div>
      </AppLayout>

      <ShareProject/>
    </>
  );
};
